import { useState, useEffect, useRef } from 'react';

const plans = [
  {
    badge: 'Pulso Mensual',
    price: '100',
    currency: '€',
    period: 'por mes',
    desc: 'Acceso mensual renovable a todos los módulos activos de tu nivel.',
    features: [
      'Acceso completo a tu nivel actual',
      'Video, PDF e Infografías por módulo',
      'Quiz automatizado con retroalimentación',
      'Comunidad privada de la Red',
      'App Estelarte (versión gratuita)',
      'Soporte por mensajería',
    ],
    cta: 'Iniciar Trazado Mensual',
    featured: false,
    accent: '#8B9D83',
  },
  {
    badge: 'Blueprint Completo',
    price: '500',
    currency: '€',
    period: '6 meses de acceso completo',
    desc: 'La experiencia completa del sistema. Acceso total, sin interrupciones.',
    features: [
      'Acceso a los 6 Pilares completos',
      'Desbloqueo secuencial por Quiz',
      'Sesión de integración en grupo (1/mes)',
      'App Estelarte (versión gratuita)',
      'Comunidad VIP de la Red Geodésica',
      'Certificado de Alineación Nodal',
    ],
    cta: 'Ocupar mi Nodo Completo',
    featured: true,
    accent: '#C97B63',
  },
  {
    badge: 'Acceso Corporativo',
    price: 'Custom',
    currency: '',
    period: 'Diseñado a medida',
    desc: 'Para equipos y organizaciones que buscan Reparación Sistémica colectiva.',
    features: [
      'Licencias múltiples (5+ personas)',
      'Dashboard de progreso grupal',
      'Sesiones privadas de constelación',
      'Personalización del recorrido',
      'App Estelarte (versión PRO)',
      'Consultoría de Bio-habitabilidad',
    ],
    cta: 'Solicitar Propuesta',
    featured: false,
    accent: '#F8F6F3',
  },
];

export default function PlanesSection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleCta = (plan: (typeof plans)[number]) => {
    if (plan.badge === 'Acceso Corporativo') {
      document.querySelector('#iniciar-trazado')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      document.querySelector('#iniciar-trazado')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="planes" ref={sectionRef} className="bg-[#2A2D34] py-32 px-6 overflow-hidden">
      <div className="max-w-screen-xl mx-auto">
        {/* Header */}
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-[#8B9D83] text-xs uppercase tracking-[4px] mb-4">— Planes de Acceso —</p>
          <h2
            className="text-[#F8F6F3] font-bold leading-tight mb-4"
            style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)' }}
          >
            Elige tu Nivel de Acceso
          </h2>
          <p className="text-[#8B9D83] text-base">
            Todos los planes incluyen acceso gratuito a la{' '}
            <strong className="text-[#C97B63]">App Estelarte</strong> para mapeo sistémico.
          </p>
        </div>

        {/* Cards */}
        <div
          className={`grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-1000 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          {plans.map((plan) => (
            <div
              key={plan.badge}
              className={`relative rounded-2xl p-10 flex flex-col transition-all duration-300 ${
                plan.featured
                  ? 'bg-[#C97B63]/10 border-2 border-[#C97B63]/50'
                  : 'bg-[#353942] border border-[#F8F6F3]/10 hover:border-[#F8F6F3]/25'
              }`}
            >
              {plan.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C97B63] text-[#F8F6F3] text-xs font-semibold px-4 py-1 rounded-full whitespace-nowrap">
                  ⊹ Más Elegido
                </div>
              )}

              {/* Badge */}
              <div className="mb-6">
                <span
                  className="text-xs uppercase tracking-[2px] font-semibold px-3 py-1.5 rounded-full"
                  style={{
                    backgroundColor: `${plan.accent}20`,
                    color: plan.accent,
                  }}
                >
                  {plan.badge}
                </span>
              </div>

              {/* Price */}
              <div className="mb-3 flex items-start gap-1">
                {plan.currency && (
                  <span className="text-[#F8F6F3]/60 text-xl mt-2">{plan.currency}</span>
                )}
                <span
                  className="font-bold text-[#F8F6F3] leading-none"
                  style={{ fontSize: plan.price === 'Custom' ? '2.5rem' : '4rem' }}
                >
                  {plan.price}
                </span>
              </div>
              <p className="text-[#F8F6F3]/40 text-xs uppercase tracking-[1px] mb-4">{plan.period}</p>
              <p className="text-[#F8F6F3]/60 text-sm leading-relaxed mb-8">{plan.desc}</p>

              {/* Features */}
              <ul className="space-y-3 mb-10 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-xs" style={{ color: plan.accent }} />
                    </div>
                    <span className="text-[#F8F6F3]/70 text-sm leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={() => handleCta(plan)}
                className="w-full py-4 rounded-xl text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={
                  plan.featured
                    ? { backgroundColor: '#C97B63', color: '#F8F6F3' }
                    : {
                        backgroundColor: 'transparent',
                        color: plan.accent,
                        border: `2px solid ${plan.accent}40`,
                      }
                }
                onMouseEnter={(e) => {
                  if (!plan.featured) {
                    (e.target as HTMLButtonElement).style.borderColor = plan.accent;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!plan.featured) {
                    (e.target as HTMLButtonElement).style.borderColor = `${plan.accent}40`;
                  }
                }}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Fine print */}
        <p className="text-center text-[#F8F6F3]/30 text-xs mt-12 tracking-wide">
          Garantía de recalibración nodal de 14 días · Pagos seguros mediante Stripe & PayPal
        </p>
      </div>
    </section>
  );
}
